import Vue from 'vue';
import Router from 'vue-router';
import _ from 'lodash';

import pinia from '@/pinia';
import { useAuthStore, useSetupStore } from '@/stores';
import routes from './routes';

Vue.use(Router);

const createRouter = () =>
    new Router({
        mode: 'history',
        scrollBehavior: () => ({ x: 0, y: 0, behavior: 'smooth' }),
        linkActiveClass: 'active',
        base: __dirname,
        routes
    });

const router = createRouter();

// Redirect users without any CMS to the setup wizard
router.beforeEach((to, from, next) => {
    const authStore = useAuthStore(pinia);
    const setupStore = useSetupStore(pinia);
    const activeCmsId = _.get(setupStore.activeCms, 'id');
    const isSuperUser = _.get(authStore.user, 'super_user', false);
    const isLoaded = setupStore.loaded;
    const hasCmss = setupStore.cmss && setupStore.cmss.length > 0;

    // Routes flagged as super-user-only - allow super-users or super-users
    // currently impersonating another admin, as the server-sent session says.
    // Only enforce once the auth state is settled (user hydrated). Otherwise
    // a super-user refreshing the page would briefly fail isSuperUser and get
    // redirected away. If the route still loads for a non-authorized user,
    // the GraphQL authorize blocks access and the page renders a 403.
    const userHydrated = !!_.get(authStore.user, 'id');
    if (_.get(to, 'meta.requiresSuperUser') && userHydrated) {
        if (!isSuperUser && !authStore.isImpersonatedBySuperUser) {
            next({ name: 'system-account-form' });
            return;
        }
    }

    // Skip check for setup wizard route, auth routes, public routes, and account routes
    const isAccountRoute = to.path.startsWith('/account');
    const isPublicRoute = _.get(to, 'meta.public', false);
    if (to.name === 'cms-setup-wizard' || to.name === 'system-cms-wizard' || to.meta.context === 'auth' || isPublicRoute || isAccountRoute) {
        next();
        return;
    }

    // Skip check when navigating away from setup wizard (CMS was just created)
    if (from.name === 'cms-setup-wizard' || from.name === 'system-cms-wizard') {
        next();
        return;
    }

    // Skip CMS checks if user is not authenticated
    if (!authStore.isAuthenticated) {
        next();
        return;
    }

    // Skip CMS checks until setup data is loaded
    if (!isLoaded) {
        next();
        return;
    }

    // Redirect to setup wizard if user has no CMS at all (except super users)
    if (!hasCmss && !isSuperUser) {
        next({ name: 'cms-setup-wizard', params: { cms: activeCmsId } });
        return;
    }

    next();
});

router.afterEach(() => {
    // finish the progress bar
    // router.app.$Progress.finish()

    const setupStore = useSetupStore(pinia);
    setupStore.checkHealth();
});

// const waitForStorageToBeReady = async (to, from, next) => {
//     await store.restored
//     next()
// }
// router.beforeEach(waitForStorageToBeReady)

export function resetRouter() {
    const newRouter = createRouter();
    router.matcher = newRouter.matcher;
}

export default router;
